import React from "react";
import { RadioGroup, RadioGroupItem } from "./radio-group";
import { Card, CardHeader, CardTitle, CardDescription } from "./card";
import { Label } from "./label";
import { cn } from "../../lib/utils";

interface RadioOption {
	value: string;
	title: string;
	description: string;
	disabled?: boolean;
	error?: boolean;
}

interface RadioButtonGroupProps {
	options: RadioOption[];
	value?: string;
	defaultValue?: string;
	onValueChange?: (value: string) => void;
	className?: string;
	renderItem?: (props: RadioButtonRenderProps) => React.ReactNode;
}

interface RadioButtonRenderProps {
	option: RadioOption;
	radioElement: React.ReactNode;
}

function RadioButtonGroup({
	options,
	value,
	defaultValue,
	onValueChange,
	className,
	renderItem,
}: RadioButtonGroupProps) {
	return (
		<RadioGroup
			value={value}
			defaultValue={defaultValue}
			onValueChange={onValueChange}
			className={cn("space-y-3", className)}
		>
			{options.map((option) => {
				if (renderItem) {
					const radioElement = (
						<RadioGroupItem
							value={option.value}
							id={option.value}
							disabled={option.disabled}
							className={cn(
								"border-2",
								option.disabled && "border-gray-300 text-gray-300",
								option.error &&
									!option.disabled &&
									"border-red-500 text-red-500",
								!option.disabled &&
									!option.error &&
									"data-[state=checked]:border-alta-light-green data-[state=checked]:text-alta-light-green data-[state=checked]:[&>span>svg]:fill-alta-light-green",
							)}
						/>
					);

					const handleCustomClick = (e: React.MouseEvent) => {
						if (option.disabled) {
							e.preventDefault();
							e.stopPropagation();
						}
					};

					return (
						<div key={option.value} onClick={handleCustomClick}>
							{renderItem({
								option,
								radioElement,
							})}
						</div>
					);
				}

				return <RadioButtonItem key={option.value} option={option} />;
			})}
		</RadioGroup>
	);
}

interface RadioButtonItemProps {
	option: RadioOption;
	renderItem?: (props: RadioButtonRenderProps) => React.ReactNode;
}

function RadioButtonItem({ option, renderItem }: RadioButtonItemProps) {
	const { value, title, description, disabled, error } = option;

	const radioElement = (
		<RadioGroupItem
			value={value}
			id={value}
			disabled={disabled}
			className={cn(
				"border-2",
				disabled && "border-gray-300 text-gray-300",
				error && !disabled && "border-red-500 text-red-500",
				!disabled &&
					!error &&
					"data-[state=checked]:border-alta-light-green data-[state=checked]:text-alta-light-green data-[state=checked]:[&>span>svg]:fill-alta-light-green",
			)}
		/>
	);

	// Use custom render if provided
	if (renderItem) {
		return <>{renderItem({ option, radioElement })}</>;
	}

	// Default rendering - use CSS data attributes for selected state
	const getCardClassName = () => {
		let classes = "cursor-pointer transition-all duration-200 hover:shadow-md";

		if (disabled) {
			classes += " opacity-50 cursor-not-allowed bg-gray-50 border-gray-300";
		} else if (error) {
			classes += " border-red-500";
		} else {
			classes +=
				" border-gray-200 hover:border-gray-300 focus-within:border-alta-light-green focus-within:ring-2 focus-within:ring-alta-light-green/20";
			// Use data attribute selector for selected state
			classes += " has-[[data-state=checked]]:border-alta-light-green";
		}

		return classes;
	};

	const getTitleClassName = () => {
		if (disabled) {
			return "text-gray-400";
		}
		return "text-gray-900";
	};

	const getDescriptionClassName = () => {
		if (disabled) {
			return "text-gray-300";
		}
		return "text-gray-500";
	};

	const handleClick = (e: React.MouseEvent) => {
		if (disabled) {
			e.preventDefault();
			return;
		}
		// Let the radio input handle the change naturally
	};

	return (
		<Label
			htmlFor={value}
			className={disabled ? "cursor-not-allowed" : "cursor-pointer"}
			onClick={handleClick}
		>
			<Card className={getCardClassName()}>
				<CardHeader className="flex flex-row items-center space-x-4 py-4">
					{radioElement}
					<div className="flex-1 space-y-2">
						<CardTitle
							className={cn("text-sm font-medium", getTitleClassName())}
						>
							{title}
						</CardTitle>
						<CardDescription
							className={cn("text-xs", getDescriptionClassName())}
						>
							{description}
						</CardDescription>
					</div>
				</CardHeader>
			</Card>
		</Label>
	);
}

export { RadioButtonGroup, RadioButtonItem, type RadioButtonRenderProps, type RadioOption };
